'use client';

import type { CSSProperties } from 'react';
import { useRef } from 'react';
import { useColumnCount } from '../core/useColumnCount';
import { tonesOf } from './data/buckets';
import { useBuckets } from './data/useBuckets';
import styles from './Histogram.module.css';
import { Columns } from './parts/Columns';
import { TICK_BAND, Ticks, type TickUnit } from './parts/Ticks';
import { sweepVars } from './sweep';
import type { HistogramData, HistogramHighlight } from './types';

export const Histogram = ({
  data,
  bucketSeconds,
  filterIds = null,
  highlights = null,
  activeHighlight = null,
  height = 18,
  scaleToAll = false,
  showTicks = true,
  tickUnit = 'year',
  flipped = false,
  className,
  ariaLabel,
}: {
  data: HistogramData;
  /** the data's native bucket width; columns snap to it so consecutive
      buckets render adjacent instead of spreading across the pixel grid */
  bucketSeconds?: number;
  filterIds?: string[] | null;
  highlights?: HistogramHighlight[] | null;
  activeHighlight?: string | null;
  height?: number;
  scaleToAll?: boolean;
  showTicks?: boolean;
  tickUnit?: TickUnit;
  flipped?: boolean;
  className?: string;
  ariaLabel?: string;
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const pixelColumns = useColumnCount(rootRef);
  const n = bucketSeconds
    ? Math.min(
        pixelColumns,
        Math.max(1, Math.ceil((data.end - data.start) / bucketSeconds))
      )
    : pixelColumns;
  const { base, layers, yScale } = useBuckets({
    data,
    n,
    height,
    filterIds,
    highlights,
    scaleToAll,
  });

  const toneList = tonesOf(data);
  const belowBaseline = showTicks ? TICK_BAND : 0;
  const wrapHeight = height + belowBaseline;

  return (
    <div
      className={`${styles.wrap}${className ? ` ${className}` : ''}`}
      ref={rootRef}
      role="img"
      aria-label={ariaLabel ?? 'Activity over time'}
      data-flipped={flipped || undefined}
      style={
        {
          '--below': `${belowBaseline}px`,
          ...sweepVars(n),
        } as CSSProperties
      }
    >
      <div
        className={styles.bars}
        style={{ height: `${wrapHeight}px` }}
        data-dimmed={activeHighlight != null || undefined}
      >
        <Columns buckets={base} toneList={toneList} yScale={yScale} />
      </div>

      {layers.map((layer) => (
        <div
          key={layer.key}
          className={styles.layer}
          data-active={layer.key === activeHighlight || undefined}
          aria-hidden={true}
        >
          <Columns
            buckets={layer.buckets}
            toneList={toneList}
            yScale={yScale}
            sparse={true}
          />
        </div>
      ))}

      {showTicks && (
        <Ticks start={data.start} end={data.end} n={n} unit={tickUnit} />
      )}
    </div>
  );
};
